import { get, update } from "idb-keyval";
import type { ReplayFailure } from "@/lib/mutationRegistry";

const KEY = "ruralops-offline-queue";

// Lightweight pub/sub so useOfflineQueue re-reads IDB whenever the queue changes.
type Listener = () => void;
const listeners = new Set<Listener>();
export function subscribeToQueue(fn: Listener): () => void {
	listeners.add(fn);
	return () => listeners.delete(fn);
}
function notifyQueueChanged() {
	for (const fn of listeners) fn();
}

export interface QueueItem {
	id: string;
	offlineQueueKey: string;
	payload: unknown;
	/** Schema version of the payload at enqueue time. */
	payloadVersion: number;
	enqueuedAt: number;
	retries: number;
	/** Absent only on items queued before the queue was scoped per user. */
	userId?: string;
	/**
	 * Serializable failure snapshot from the last replay, resolved to a localized
	 * string at render time. `string` is tolerated for legacy items persisted
	 * before this envelope change (read path only — new writes are `ReplayFailure`).
	 */
	lastError?: ReplayFailure | string;
}

// The queue is one IndexedDB key shared by every user of this browser, so a
// user who signs out and a different user who signs in see the same store.
// Reads and clears are therefore scoped to the owner the app sets here.
let ownerId: string | null = null;

export function setQueueOwner(userId: string | null): void {
	if (userId === ownerId) return;
	ownerId = userId;
	notifyQueueChanged();
}

// Items queued before per-user scoping have no owner. Showing them to whoever is
// signed in keeps them syncable or discardable instead of stranding them.
function isOwnedByCurrentUser(item: QueueItem): boolean {
	return item.userId === undefined || item.userId === ownerId;
}

// The reload gate counts every queued write in the browser, whoever queued it:
// a reload must not drop any user's pending work (ADR
// 2026-08-19-pwa-own-the-sw-client-and-gate-every-reload).
export async function listAllQueueItems(): Promise<QueueItem[]> {
	return (await get<QueueItem[]>(KEY)) ?? [];
}

// Queue mutations go through `update` so each read-modify-write is one IndexedDB
// transaction: concurrent replays of different items must not overwrite each other.
export async function enqueue(
	offlineQueueKey: string,
	payload: unknown,
	payloadVersion: number,
): Promise<QueueItem> {
	if (ownerId === null) {
		throw new Error("Cannot queue an offline write without a signed-in user");
	}
	const item: QueueItem = {
		id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
		offlineQueueKey,
		payload,
		payloadVersion,
		enqueuedAt: Date.now(),
		retries: 0,
		userId: ownerId,
	};
	await update<QueueItem[]>(KEY, (current) => [...(current ?? []), item]);
	notifyQueueChanged();
	return item;
}

export async function listQueue(): Promise<QueueItem[]> {
	if (ownerId === null) return [];
	return (await listAllQueueItems()).filter(isOwnedByCurrentUser);
}

export async function removeQueueItem(id: string): Promise<void> {
	await update<QueueItem[]>(KEY, (items) =>
		(items ?? []).filter((i) => i.id !== id),
	);
	notifyQueueChanged();
}

export async function updateQueueItem(
	id: string,
	patch: Partial<Pick<QueueItem, "retries" | "lastError">>,
): Promise<void> {
	await update<QueueItem[]>(KEY, (items) =>
		(items ?? []).map((i) => (i.id === id ? { ...i, ...patch } : i)),
	);
	notifyQueueChanged();
}

export async function clearQueue(): Promise<void> {
	await update<QueueItem[]>(KEY, (items) =>
		(items ?? []).filter((i) => !isOwnedByCurrentUser(i)),
	);
	notifyQueueChanged();
}
